import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { downloadRiskRegisterPDF, downloadSPIPDF, downloadCompliancePDF, createScheduledReport, getUsers } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  BarChart3, Download, FileWarning, AlertTriangle, ClipboardCheck, 
  Activity, Building2, Calendar as CalendarIcon, Filter, FileText,
  TrendingUp, Shield, Users, Plane, CheckCircle2, Clock,
  ChevronRight, Sparkles
} from 'lucide-react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { cn } from '@/lib/utils';

const REPORT_TYPES = [
  { 
    id: 'risk_register', 
    icon: AlertTriangle, 
    title: 'Risk Register Report', 
    description: 'Complete risk register with all active risks and mitigation plans',
    color: 'text-orange-400', 
    bgColor: 'bg-orange-600/20',
    category: 'safety'
  },
  { 
    id: 'spi_dashboard', 
    icon: Activity, 
    title: 'SPI Dashboard Report', 
    description: 'Safety Performance Indicators overview and trends',
    color: 'text-sky-400', 
    bgColor: 'bg-sky-600/20',
    category: 'performance'
  },
  { 
    id: 'compliance_gaca', 
    icon: Building2, 
    title: 'GACA Compliance Report', 
    description: 'Saudi GACA regulatory compliance status',
    color: 'text-emerald-400', 
    bgColor: 'bg-emerald-600/20',
    category: 'compliance'
  },
  { 
    id: 'compliance_easa', 
    icon: Building2, 
    title: 'EASA Compliance Report', 
    description: 'European EASA regulatory compliance status',
    color: 'text-violet-400', 
    bgColor: 'bg-violet-600/20',
    category: 'compliance'
  },
  { 
    id: 'incident_summary', 
    icon: FileWarning, 
    title: 'Incident Summary Report', 
    description: 'Summary of all incidents within selected date range',
    color: 'text-red-400', 
    bgColor: 'bg-red-600/20',
    category: 'safety'
  },
  { 
    id: 'capa_status', 
    icon: ClipboardCheck, 
    title: 'CAPA Status Report', 
    description: 'Corrective and Preventive Actions status overview',
    color: 'text-amber-400', 
    bgColor: 'bg-amber-600/20',
    category: 'safety'
  },
  { 
    id: 'audit_summary', 
    icon: Shield, 
    title: 'Audit Summary Report', 
    description: 'Completed and planned audits with findings',
    color: 'text-indigo-400', 
    bgColor: 'bg-indigo-600/20',
    category: 'compliance'
  },
  { 
    id: 'training_completion', 
    icon: Users, 
    title: 'Training Completion Report', 
    description: 'Training program completion rates by department',
    color: 'text-pink-400', 
    bgColor: 'bg-pink-600/20',
    category: 'performance'
  },
];

const DATE_PRESETS = [
  { label: 'Last 7 Days', getValue: () => ({ from: subDays(new Date(), 7), to: new Date() }) },
  { label: 'Last 30 Days', getValue: () => ({ from: subDays(new Date(), 30), to: new Date() }) },
  { label: 'Last 90 Days', getValue: () => ({ from: subDays(new Date(), 90), to: new Date() }) },
  { label: 'This Month', getValue: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
  { label: 'Last Month', getValue: () => ({ from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) }) },
  { label: 'This Year', getValue: () => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }) },
  { label: 'Last Year', getValue: () => ({ from: startOfYear(subMonths(new Date(), 12)), to: endOfYear(subMonths(new Date(), 12)) }) },
];

export default function ReportsPage() {
  const { t } = useTranslation();
  const { token, api, user } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [dateRange, setDateRange] = useState({ from: subDays(new Date(), 30), to: new Date() });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('Last 30 Days');
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [selectedReportForSchedule, setSelectedReportForSchedule] = useState(null);
  const [users, setUsers] = useState([]);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'weekly',
    day_of_week: 'mon',
    day_of_month: '1',
    hour: '8',
    recipients: []
  });

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.label);
    setDateRange(preset.getValue());
  };

  const openScheduleDialog = async (report) => {
    setSelectedReportForSchedule(report);
    setScheduleForm({
      frequency: 'weekly',
      day_of_week: 'mon',
      day_of_month: '1',
      hour: '8',
      recipients: []
    });
    
    // Fetch users for recipient selection
    try {
      const response = await getUsers(api);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
    
    setShowScheduleDialog(true);
  };

  const toggleRecipient = (userId) => {
    setScheduleForm(prev => ({
      ...prev,
      recipients: prev.recipients.includes(userId)
        ? prev.recipients.filter(id => id !== userId)
        : [...prev.recipients, userId]
    }));
  };

  const handleCreateSchedule = async () => {
    if (!selectedReportForSchedule || scheduleForm.recipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    
    setIsScheduling(true);
    try {
      await createScheduledReport(api, {
        report_type: selectedReportForSchedule.id,
        frequency: scheduleForm.frequency,
        export_format: 'pdf',
        recipients: scheduleForm.recipients,
        hour: parseInt(scheduleForm.hour),
        day_of_week: scheduleForm.day_of_week,
        day_of_month: parseInt(scheduleForm.day_of_month)
      });
      
      toast.success('Report scheduled successfully!');
      setShowScheduleDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule report');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleDownload = async (reportId) => {
    setDownloading(reportId);
    try {
      let response;
      let filename;
      const dateStr = format(new Date(), 'yyyyMMdd');
      
      switch (reportId) {
        case 'risk_register': 
          response = await downloadRiskRegisterPDF(token); 
          filename = `risk_register_${dateStr}.pdf`; 
          break;
        case 'spi_dashboard': 
          response = await downloadSPIPDF(token); 
          filename = `spi_dashboard_${dateStr}.pdf`; 
          break;
        case 'compliance_gaca': 
          response = await downloadCompliancePDF(token, 'gaca'); 
          filename = `gaca_compliance_${dateStr}.pdf`; 
          break;
        case 'compliance_easa': 
          response = await downloadCompliancePDF(token, 'easa'); 
          filename = `easa_compliance_${dateStr}.pdf`; 
          break;
        case 'incident_summary':
          response = await downloadRiskRegisterPDF(token);
          filename = `incident_summary_${dateStr}.pdf`;
          break;
        case 'capa_status':
          response = await downloadRiskRegisterPDF(token);
          filename = `capa_status_${dateStr}.pdf`;
          break;
        case 'audit_summary':
          response = await downloadRiskRegisterPDF(token);
          filename = `audit_summary_${dateStr}.pdf`;
          break;
        case 'training_completion':
          response = await downloadRiskRegisterPDF(token);
          filename = `training_completion_${dateStr}.pdf`;
          break;
        default: 
          setDownloading(null);
          return;
      }
      
      // Check if response has data
      if (!response || !response.data) {
        throw new Error('No data received from server');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download report:', error);
      // Show user-friendly error message
      alert(`Failed to download report: ${error.message || 'Unknown error'}`);
    } finally {
      setDownloading(null);
    }
  };

  const filteredReports = activeTab === 'all' 
    ? REPORT_TYPES 
    : REPORT_TYPES.filter(r => r.category === activeTab);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="reports-title">
            {t('common.reports')}
          </h1>
          <p className="text-slate-400 mt-1">Generate and download safety management reports</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"
          data-testid="toggle-filters-btn"
        >
          <Filter className="w-4 h-4 mr-2" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </div>

      {/* Date Range Filters */}
      {showFilters && (
        <Card className="bg-slate-900/80 border-slate-700 animate-fade-in" data-testid="date-filters">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-sky-400" />
              Report Date Range
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-end gap-6">
              {/* Presets */}
              <div className="flex-1 min-w-[200px]">
                <Label className="text-slate-400 text-sm mb-2 block">Quick Select</Label>
                <div className="flex flex-wrap gap-2">
                  {DATE_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      size="sm"
                      variant={selectedPreset === preset.label ? "default" : "outline"}
                      onClick={() => handlePresetSelect(preset)}
                      className={cn(
                        "text-xs",
                        selectedPreset === preset.label 
                          ? "bg-sky-600 hover:bg-sky-700" 
                          : "border-slate-700 text-slate-300 hover:bg-slate-800"
                      )}
                      data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Date Range */}
              <div className="flex gap-4">
                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">From Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[180px] justify-start text-left font-normal border-slate-700",
                          !dateRange.from && "text-slate-500"
                        )}
                        data-testid="from-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.from ? format(dateRange.from, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700">
                      <Calendar
                        mode="single"
                        selected={dateRange.from}
                        onSelect={(date) => {
                          setDateRange(prev => ({ ...prev, from: date }));
                          setSelectedPreset('Custom');
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label className="text-slate-400 text-sm mb-2 block">To Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[180px] justify-start text-left font-normal border-slate-700",
                          !dateRange.to && "text-slate-500"
                        )}
                        data-testid="to-date-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.to ? format(dateRange.to, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700">
                      <Calendar
                        mode="single"
                        selected={dateRange.to}
                        onSelect={(date) => {
                          setDateRange(prev => ({ ...prev, to: date }));
                          setSelectedPreset('Custom');
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Selected Range Display */}
            <div className="mt-4 p-3 bg-slate-800/50 rounded-lg flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span className="text-slate-300 text-sm">
                Reports will include data from{' '}
                <span className="text-white font-medium">{format(dateRange.from, 'MMM d, yyyy')}</span>
                {' '}to{' '}
                <span className="text-white font-medium">{format(dateRange.to, 'MMM d, yyyy')}</span>
              </span>
              <Badge variant="outline" className="ml-auto border-slate-600 text-slate-400">
                {selectedPreset}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Categories Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-900/60 border border-slate-800 p-1">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-all"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            All Reports
          </TabsTrigger>
          <TabsTrigger 
            value="safety"
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-safety"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Safety
          </TabsTrigger>
          <TabsTrigger 
            value="compliance"
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-compliance"
          >
            <Shield className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger 
            value="performance"
            className="data-[state=active]:bg-slate-700 data-[state=active]:text-white"
            data-testid="tab-performance"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredReports.map((report) => {
              const Icon = report.icon;
              const isDownloading = downloading === report.id;
              
              return (
                <Card 
                  key={report.id} 
                  className="bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-all hover:shadow-lg hover:shadow-slate-900/50"
                  data-testid={`report-card-${report.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-xl ${report.bgColor} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-7 h-7 ${report.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-white">{report.title}</h3>
                            <Badge 
                              variant="outline" 
                              className="mt-1 text-xs capitalize border-slate-700 text-slate-400"
                            >
                              {report.category}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-slate-400 text-sm mt-2">{report.description}</p>
                        
                        <div className="flex items-center gap-3 mt-4">
                          <Button 
                            onClick={() => handleDownload(report.id)} 
                            disabled={isDownloading}
                            className="bg-slate-800 hover:bg-slate-700 text-white flex-1"
                            data-testid={`download-${report.id}`}
                          >
                            {isDownloading ? (
                              <>
                                <span className="spinner w-4 h-4 mr-2" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-2" />
                                Download PDF
                              </>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                            title="Schedule Report"
                            onClick={() => openScheduleDialog(report)}
                            data-testid={`schedule-${report.id}`}
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Report Generation Info */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 text-slate-400 text-sm">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <p>
              Reports are generated in real-time with the latest data. 
              Use date filters to customize the reporting period for time-sensitive reports.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Report Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Clock className="h-5 w-5 text-sky-400" />
              Schedule Report
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedReportForSchedule?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Frequency */}
            <div>
              <Label className="text-slate-300">Frequency</Label>
              <Select 
                value={scheduleForm.frequency} 
                onValueChange={(v) => setScheduleForm(prev => ({...prev, frequency: v}))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Day of Week (for weekly) */}
            {scheduleForm.frequency === 'weekly' && (
              <div>
                <Label className="text-slate-300">Day of Week</Label>
                <Select 
                  value={scheduleForm.day_of_week} 
                  onValueChange={(v) => setScheduleForm(prev => ({...prev, day_of_week: v}))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="mon">Monday</SelectItem>
                    <SelectItem value="tue">Tuesday</SelectItem>
                    <SelectItem value="wed">Wednesday</SelectItem>
                    <SelectItem value="thu">Thursday</SelectItem>
                    <SelectItem value="fri">Friday</SelectItem>
                    <SelectItem value="sat">Saturday</SelectItem>
                    <SelectItem value="sun">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Day of Month (for monthly/quarterly) */}
            {(scheduleForm.frequency === 'monthly' || scheduleForm.frequency === 'quarterly') && (
              <div>
                <Label className="text-slate-300">Day of Month</Label>
                <Select 
                  value={scheduleForm.day_of_month} 
                  onValueChange={(v) => setScheduleForm(prev => ({...prev, day_of_month: v}))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {[1,5,10,15,20,25].map(day => (
                      <SelectItem key={day} value={day.toString()}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Time */}
            <div>
              <Label className="text-slate-300">Time (Hour)</Label>
              <Select 
                value={scheduleForm.hour} 
                onValueChange={(v) => setScheduleForm(prev => ({...prev, hour: v}))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {[6,7,8,9,10,11,12,14,16,18].map(h => (
                    <SelectItem key={h} value={h.toString()}>{h}:00</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Recipients */}
            <div>
              <Label className="text-slate-300">Recipients</Label>
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1 border border-slate-700 rounded-lg p-2 bg-slate-800/50">
                {users.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-2">Loading users...</p>
                ) : (
                  users.map(u => (
                    <div 
                      key={u.id}
                      onClick={() => toggleRecipient(u.id)}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded cursor-pointer transition-colors",
                        scheduleForm.recipients.includes(u.id) 
                          ? "bg-sky-600/20 border border-sky-500/50" 
                          : "hover:bg-slate-700/50"
                      )}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded border flex items-center justify-center",
                        scheduleForm.recipients.includes(u.id) 
                          ? "bg-sky-500 border-sky-500" 
                          : "border-slate-600"
                      )}>
                        {scheduleForm.recipients.includes(u.id) && (
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-slate-300 text-sm">{u.name}</span>
                      <Badge variant="outline" className="text-xs border-slate-600 text-slate-400 ml-auto">
                        {u.role}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {scheduleForm.recipients.length} recipient(s) selected
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)} className="border-slate-700">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSchedule} 
              disabled={isScheduling || scheduleForm.recipients.length === 0}
              className="bg-sky-600 hover:bg-sky-700"
            >
              {isScheduling ? 'Scheduling...' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
